// Which card of an Option Select Item group to preselect.
//
// A group is every item on screen that writes the same choice variable. A card
// hidden by a visibility rule is unmounted, so it never takes part — which is
// what lets an author swap the offers on screen with rules and still get one of
// the visible ones picked.
//
// Import-free so the sibling test runs under bare `node --test`; the equality
// predicate (`valuesMatch`) is passed in rather than imported.

export interface PreselectCandidate {
  /** What picking the card writes. `undefined` / `null`: a card that can never be picked. */
  value: unknown
  /** The Priority field as stored, whatever it holds. */
  priority: unknown
  /** The "Start selected" tick. */
  startSelected: boolean
  multi: boolean
}

/** What the group last picked on its own — forgotten as soon as the visitor picks. */
export interface AutoPick {
  value: unknown
}

export type ValuesMatch = (a: unknown, b: unknown) => boolean

/**
 * The card's priority, or undefined when it has none that counts. The editor
 * lists Priority only under a ticked Start selected on a single-select card,
 * and a value left behind when either changes is ignored here too — otherwise
 * it would go on deciding the group with no field on screen showing it.
 */
export function priorityOf(c: PreselectCandidate): number | undefined {
  if (!c.startSelected || c.multi) return undefined
  return typeof c.priority === 'number' && Number.isFinite(c.priority) ? c.priority : undefined
}

/**
 * Priorities take over a group as soon as one card on screen has one. A group
 * without any keeps the older "Start selected" behaviour, which the item runs
 * on its own — so pipes authored before priorities, and every multi-select
 * group, behave exactly as they did.
 */
export function usesPriorities(candidates: readonly PreselectCandidate[]): boolean {
  return candidates.some((c) => priorityOf(c) !== undefined)
}

// Only a single-select "Start selected" card is ever preselected; one without a
// priority ranks below every priority.
function rankOf(c: PreselectCandidate): number | undefined {
  if (!c.startSelected || c.multi || c.value === undefined || c.value === null) return undefined
  return priorityOf(c) ?? -Infinity
}

/** Index of the highest-ranked card — the first on the page on a tie — or -1 when none ranks. */
export function preselectWinner(candidates: readonly PreselectCandidate[]): number {
  let best = -1
  let bestRank = -Infinity
  candidates.forEach((c, i) => {
    const rank = rankOf(c)
    if (rank === undefined) return
    if (best === -1 || rank > bestRank) {
      best = i
      bestRank = rank
    }
  })
  return best
}

/** The item's own `isSelected`: a single card matches the stored value, a multi card a member of it. */
export function isShownPicked(c: PreselectCandidate, stored: unknown, match: ValuesMatch): boolean {
  return c.multi
    ? Array.isArray(stored) && stored.some((v) => match(v, c.value))
    : match(stored, c.value)
}

/**
 * The index of the card whose value a group with priorities stores, or -1 to
 * leave the choice as it is.
 *
 * - A pick the visitor made that is still on screen is kept.
 * - Anything else that no card on screen shows as picked — nothing stored yet,
 *   a pick whose card a rule has since hidden, a value restored from another
 *   pipe — is replaced by the card with the highest priority.
 * - The group's own earlier pick is ranked again, so a card with a higher
 *   priority that comes on screen later takes over, until the visitor picks.
 */
export function planPreselect(
  candidates: readonly PreselectCandidate[],
  stored: unknown,
  auto: AutoPick | undefined,
  match: ValuesMatch,
): number {
  if (!usesPriorities(candidates)) return -1
  const index = preselectWinner(candidates)
  if (index === -1 || match(stored, candidates[index]!.value)) return -1
  const somethingShownPicked = candidates.some((c) => isShownPicked(c, stored, match))
  const ownPick = auto !== undefined && match(stored, auto.value)
  return somethingShownPicked && !ownPick ? -1 : index
}
